import React, {useRef} from 'react';
import {Col, Container, Row} from 'react-bootstrap';
import PageTitle from '../../../../uiToolkit/PageTitle';
import SearchInput from '../../../../uiToolkit/Inputs/SearchInput';
import Button from '../../../../uiToolkit/Buttons/Button';
import LoadingSpinner from '../../../../uiToolkit/LoadingSpinner/LoadingSpinner';
import FeedbackEmptyState from '../../../../uiToolkit/EmptyStates/FeedbackEmptyState';
import {Feedback} from '../../../../data/models/Feedback/Feedback';
import {feedbackText} from '../../feedbackText';
import FeedbackCard from '../components/FeedbackCard';
import styles from './FeedbackListUI.module.css';

interface Props {
  feedbackList: Feedback[];
  isLoading: boolean;
  canEdit: boolean;
  search: string;
  setSearch: (search: string) => void;
  onCreate: () => void;
  onOpen: (feedback: Feedback) => void;
  onDelete: (feedback: Feedback) => void;
  loadNextPage: () => Promise<any>;
}

const FeedbackListUI = React.memo(
  ({
    feedbackList,
    isLoading,
    canEdit,
    search,
    setSearch,
    onCreate,
    onOpen,
    onDelete,
    loadNextPage,
  }: Props) => {
    const observer = useRef<any>();
    const overscrollLock = useRef(false);

    const lastElementRef = (node: any) => {
      if (isLoading || overscrollLock.current) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          overscrollLock.current = true;
          loadNextPage().then(() => {
            overscrollLock.current = false;
          });
        }
      });

      if (node) observer.current.observe(node);
    };

    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <PageTitle title={feedbackText.title} />
          <div className={styles.filters}>
            <SearchInput value={search} onChange={setSearch} />
            {canEdit && (
              <Button
                title={feedbackText.newFeedback}
                onClick={onCreate}
                uiType="action"
                icon="icon-plus"
              />
            )}
          </div>
        </div>
        <Container fluid className={styles.list}>
          <Row>
            {feedbackList.length ? (
              feedbackList.map((feedback, index) => (
                <Col
                  xl={4}
                  lg={6}
                  key={feedback.id}
                  className={styles.col}
                  ref={
                    feedbackList.length === index + 1 ? lastElementRef : null
                  }>
                  <FeedbackCard
                    feedback={feedback}
                    canEdit={canEdit}
                    onOpen={onOpen}
                    onDelete={onDelete}
                  />
                </Col>
              ))
            ) : isLoading ? (
              <Col xl={12} lg={12}>
                <div className={styles.loading}>
                  <LoadingSpinner size={48} />
                </div>
              </Col>
            ) : (
              <Col xl={12} lg={12}>
                <FeedbackEmptyState
                  title={
                    search
                      ? feedbackText.searchEmptyTitle
                      : feedbackText.emptyTitle
                  }
                  subtitle={
                    search
                      ? feedbackText.searchEmptySubtitle
                      : feedbackText.emptySubtitle
                  }
                />
              </Col>
            )}
          </Row>
        </Container>
      </div>
    );
  },
);

export default FeedbackListUI;
